<template>
  <v-card>
    <v-card-title class="align-center justify-center third white--text mb-4">Mettre à jour la date d'expiration du contrat</v-card-title>
    <v-card-text class="pa-0">
      <div class="d-flex flex-column mb-2">
        <div>
          <p class="text-h6"><b>Date de début du contrat:</b> {{ displayValidityStart }} </p>
          <p class="text-h6"><b>Date d'expiration du contrat:</b> {{ displayValidityEnd }} </p>
        </div>
        <div class="d-flex flex-column mt-2 align-start">
          <div class="d-flex flex-shrink-0 align-center justify-center">
            <p class="text-h6 mr-2 my-0"><b>Nouvelle date d'expiration du contrat:</b></p>
            <v-menu
              v-model="menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="displayNewEndDate"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  color="third"
                />
              </template>
              <v-date-picker v-model="pickerDate" :min="minDate" no-title elevation="1">
                <v-spacer />
                <v-btn text color="third" @click="cancelPicker">Annuler</v-btn>
                <v-btn text color="third" @click="savePicker">OK</v-btn>
              </v-date-picker>
            </v-menu>
          </div>
        </div>
      </div>
      <v-col class="d-flex justify-center">
        <v-btn class="white--text" width="200" color="third" :disabled="!newEndDate || !isDateValid" :loading="loading" @click="confirm">
          Enregistrer
        </v-btn>
      </v-col>
    </v-card-text>
  </v-card>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { format, isValid, isEqual, formatISO, endOfDay, getTime } from 'date-fns';
import { sendNotification } from '@/helpers/utils';
import { updateContractEndValidity } from '@/helpers/request';
import store from '@/store';

export default {
  props: {
    contract: { type: Object, default: () => ({}) },
    customer: { type: Object, default: () => ({}) }
  },
  setup (props, { emit }) {
    const newEndDate = ref(null);
    const pickerDate = ref(null);
    const loading = ref(false);
    const menu = ref(false);
    const auxContract = ref({ ...props.contract });

    const formatISODate = (date) => {
      if (!date) return null;
      const dateObject = new Date(date.split('T')[0]);
      if (!isValid(dateObject)) return null;
      return format(dateObject, 'yyyy-MM-dd');
    };

    const formattedValidityEnd = computed(() => formatISODate(auxContract.value?.validityEnd));
    const formattedValidityStart = computed(() => formatISODate(auxContract.value?.validityStart));

    const displayValidityEnd = computed(() => {
      const d = formattedValidityEnd.value;
      return d ? format(new Date(d), 'dd/MM/yyyy') : '-';
    });

    const displayValidityStart = computed(() => {
      const d = formattedValidityStart.value;
      return d ? format(new Date(d), 'dd/MM/yyyy') : '-';
    });

    const displayNewEndDate = computed(() => {
      if (!newEndDate.value) return '';
      return format(new Date(newEndDate.value), 'dd/MM/yyyy');
    });

    const minDate = computed(() => format(new Date(), 'yyyy-MM-dd'));

    const isDateValid = computed(() => {
      if (!newEndDate.value || !formattedValidityEnd.value) return false;
      const newEndDateObject = new Date(newEndDate.value);
      const currentEnd = new Date(formattedValidityEnd.value);
      if (!isValid(newEndDateObject) || !isValid(currentEnd)) return false;
      return !isEqual(newEndDateObject, currentEnd);
    });

    const cancelPicker = () => {
      pickerDate.value = newEndDate.value;
      menu.value = false;
    };

    const savePicker = () => {
      newEndDate.value = pickerDate.value;
      menu.value = false;
    };

    const confirm = async () => {
      if (!newEndDate.value || !isDateValid.value) return;

      loading.value = true;
      const newEndDateUnixTime = getTime(endOfDay(new Date(newEndDate.value)));
      const response = await updateContractEndValidity(
        props.customer.uuid,
        auxContract.value.contractNumber,
        newEndDateUnixTime,
        store.state.user.uuid
      );
      loading.value = false;

      if (response.success && response.data) {
        sendNotification('success', "Mise à jour de la date d'expiration du contrat réussie");
        auxContract.value = { ...auxContract.value, validityEnd: formatISO(new Date(response.data.endTimestamp)) };
        emit('validity-updated', { contractNumber: auxContract.value.contractNumber, validityEnd: auxContract.value.validityEnd });
        emit('history-changed');
        newEndDate.value = formatISODate(auxContract.value.validityEnd);
        pickerDate.value = newEndDate.value;
        menu.value = false;
      } else {
        sendNotification('error', "Erreur de mise à jour de la date d'expiration du contrat");
      }
    };

    // Initialize dates
    newEndDate.value = formatISODate(props.contract?.validityEnd);
    pickerDate.value = newEndDate.value;

    return {
      newEndDate, pickerDate, loading, menu,
      displayValidityEnd, displayValidityStart, displayNewEndDate,
      minDate, isDateValid, cancelPicker, savePicker, confirm
    };
  }
};
</script>
